import React, { useState, useEffect } from 'react';
import { CreditCard, Loader2, AlertCircle, HelpCircle, X, Info, CheckCircle2 } from 'lucide-react';
import { cancelAutoReplenishment } from '../lib/stripe';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { PaymentModal } from './PaymentModal';

interface UserSettings {
  auto_replenish_enabled: boolean;
  auto_replenish_consent_date?: string;
}

export function CreditSettings() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_settings')
        .select('auto_replenish_enabled, auto_replenish_consent_date')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setSettings(data || { auto_replenish_enabled: false });
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Nie udało się załadować ustawień');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupAutoReplenishment = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async (consentDate: string) => {
    try {
      setIsProcessing(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Update user settings with consent date
      const { error: settingsError } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          auto_replenish_enabled: true,
          auto_replenish_consent_date: consentDate
        });

      if (settingsError) throw settingsError;

      await loadSettings();
      toast.success('Automatyczne uzupełnianie zostało włączone');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Nie udało się włączyć automatycznego uzupełniania');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelAutoReplenishment = async () => {
    try {
      setIsProcessing(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      await cancelAutoReplenishment(user.id);
      await loadSettings();
      toast.success('Automatyczne uzupełnianie wyłączone');
    } catch (error) {
      console.error('Error cancelling auto-replenishment:', error);
      toast.error('Nie udało się wyłączyć automatycznego uzupełniania');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-semibold">Automatyczne uzupełnianie kredytów</h2>
        </div>
        <div className="flex justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  if (settings?.auto_replenish_enabled) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-semibold">Automatyczne uzupełnianie kredytów</h2>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-800 font-medium">
                Twoje konto posiada aktywne automatyczne uzupełnianie kredytów
              </p>
              <p className="text-green-700 mt-1">
                Harmonogram postów będzie działał cały czas bez żadnych przerw.
              </p>
              {settings.auto_replenish_consent_date && (
                <p className="text-sm text-green-600 mt-2">
                  Zgoda wyrażona: {new Date(settings.auto_replenish_consent_date).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-blue-800">
              Pamiętaj o uzupełnieniu karty, aby zawsze zapewnić środki na pokrycie automatycznych obciążeń.
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleCancelAutoReplenishment}
            disabled={isProcessing}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {isProcessing ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Przetwarzanie...</span>
              </div>
            ) : (
              'Wyłącz automatyczne uzupełnianie'
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center gap-3 mb-4">
        <CreditCard className="w-6 h-6 text-blue-600" />
        <h2 className="text-lg font-semibold">Automatyczne uzupełnianie kredytów</h2>
        <button
          onClick={() => setShowInfoModal(true)}
          className="p-1 hover:bg-gray-100 rounded-full text-gray-500"
          title="Informacje o kredytach"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      <div className="prose max-w-none mb-4">
        <p>
          Włącz automatyczne uzupełnianie kredytów, aby nigdy nie zabrakło Ci środków na publikację postów.
          Gdy liczba kredytów spadnie poniżej 100, automatycznie dodamy 5000 kredytów za 199 zł.
        </p>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSetupAutoReplenishment}
          disabled={isProcessing}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {isProcessing ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Przetwarzanie...</span>
            </div>
          ) : (
            'Włącz automatyczne uzupełnianie'
          )}
        </button>
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Informacje o kredytach</h3>
              <button
                onClick={() => setShowInfoModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">AI Agent HUB - Automatyczne uzupełnianie kredytów</h4>
                <p className="text-blue-800">
                  Za 199 zł otrzymujesz 5000 kredytów, które zostaną automatycznie dodane gdy stan Twoich kredytów spadnie poniżej 100.
                </p>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">Co możesz zrobić z 5000 kredytami?</h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <span className="font-medium">Używając domyślnego webhooka (50 kredytów/post):</span>
                    <br />
                    Możesz opublikować aż 100 postów
                  </li>
                  <li>
                    <span className="font-medium">Używając własnego webhooka (25 kredytów/post):</span>
                    <br />
                    Ta sama ilość kredytów wystarczy na 200 postów
                  </li>
                </ul>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowInfoModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
}